import React, { useRef, useState } from "react";
import { App, Typography } from "antd";
import {
  CloseOutlined,
  FileExcelOutlined,
  FileDoneOutlined,
  FileOutlined,
  FilePptOutlined,
  FileWordOutlined,
  PlusOutlined,
  RotateRightOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "./Header";
import FooterLayout from "./Footer";
import PageHeader from "./PageHeader";
import OfficePdfResultActions from "./OfficePdfResultActions";
import ConversionSuccessModal from "./ConversionSuccessModal";
import "../pages/Image2Pdf/Image2Pdf.css";

const { Title, Paragraph } = Typography;

const formatFileSize = (size = 0) => {
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExtension = (fileName = "") =>
  fileName.split(".").pop()?.toLowerCase() || "";

const getOfficeFileType = (fileName = "") => {
  const extension = getFileExtension(fileName);

  if (["doc", "docx"].includes(extension)) {
    return { label: "Word", className: "word", icon: <FileWordOutlined /> };
  }

  if (["xls", "xlsx"].includes(extension)) {
    return { label: "Excel", className: "excel", icon: <FileExcelOutlined /> };
  }

  if (["ppt", "pptx"].includes(extension)) {
    return { label: "PowerPoint", className: "ppt", icon: <FilePptOutlined /> };
  }

  return { label: "Office", className: "office", icon: <FileOutlined /> };
};

const createOfficeItem = (file) => ({
  id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
    .toString(36)
    .slice(2, 8)}`,
  file,
  rotation: 0,
});

const normalizeRotation = (rotation = 0) => {
  const normalized = ((rotation % 360) + 360) % 360;
  return [0, 90, 180, 270].includes(normalized) ? normalized : 0;
};

const OfficeToPdfConverter = ({
  documentLabel,
  title,
  description,
  accept,
  emptyMessage,
}) => {
  const { message, notification } = App.useApp();
  const [fileItems, setFileItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const inputRef = useRef(null);
  const isModule = window.location.pathname.includes("/dashboard/");

  const addFiles = (incomingFiles) => {
    if (uploading) {
      return;
    }

    const nextFiles = Array.from(incomingFiles || []);
    if (nextFiles.length === 0) {
      return;
    }

    const allowedExtensions = accept
      .split(",")
      .map((item) => item.trim().replace(".", "").toLowerCase())
      .filter(Boolean);
    const validFiles = nextFiles.filter((file) =>
      allowedExtensions.includes(getFileExtension(file.name))
    );

    if (validFiles.length === 0) {
      message.error(emptyMessage);
      return;
    }

    if (validFiles.length < nextFiles.length) {
      message.warning("Some unsupported files were skipped.");
    }

    setFileItems((prev) => {
      const existing = new Set(prev.map((item) => `${item.file.name}-${item.file.size}`));
      const uniqueFiles = validFiles.filter(
        (file) => !existing.has(`${file.name}-${file.size}`)
      );

      return [...prev, ...uniqueFiles.map((file) => createOfficeItem(file))];
    });
    setResult(null);
    setShowSuccessModal(false);
  };

  const openFilePicker = () => {
    if (uploading) {
      return;
    }
    inputRef.current?.click();
  };

  const handleFileInput = (event) => {
    addFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (uploading) {
      return;
    }
    setIsDragOver(false);
    addFiles(event.dataTransfer.files);
  };

  const removeFile = (fileId) => {
    if (uploading) {
      return;
    }
    setFileItems((prev) => prev.filter((item) => item.id !== fileId));
    setResult(null);
    setShowSuccessModal(false);
  };

  const handleRotate = (fileId) => {
    if (uploading) {
      return;
    }

    setFileItems((prev) =>
      prev.map((item) =>
        item.id === fileId
          ? { ...item, rotation: (item.rotation + 90) % 360 }
          : item
      )
    );
    setResult(null);
    setShowSuccessModal(false);
  };

  const clearFiles = () => {
    if (uploading) {
      return;
    }
    setFileItems([]);
    setResult(null);
    setShowSuccessModal(false);
  };

  const handleConvert = async () => {
    if (fileItems.length === 0) {
      message.error(emptyMessage);
      return;
    }

    const formData = new FormData();
    fileItems.forEach((item) => {
      formData.append("files", item.file);
    });
    formData.append("Source", isModule ? "Web" : "Public-Web");
    formData.append(
      "rotations",
      JSON.stringify(
        fileItems.map((item, index) => ({
          index,
          fileName: item.file.name,
          rotation: normalizeRotation(item.rotation),
        }))
      )
    );

    try {
      setUploading(true);
      setResult(null);
    setShowSuccessModal(false);

      const endpoint = isModule
        ? `${process.env.REACT_APP_BASE_URL}/api/Pdf/office-to-pdf`
        : `${process.env.REACT_APP_BASE_URL}/api/WebsitePdf/website-office-to-pdf`;
      const authToken = localStorage.getItem("token") || "";
      const response = await axios.post(endpoint, formData, {
        headers: isModule ? { Authorization: `Bearer ${authToken}` } : {},
      });

      const data = response.data?.data;
      const failedFiles = data?.failedFiles || [];
      const outputType = data?.outputType || (fileItems.length > 1 ? "zip" : "pdf");

      if (!response.data?.error && data?.downloadUrl) {
        setResult({
          downloadUrl: data.downloadUrl,
          fileName:
            data.fileName ||
            (outputType === "zip" ? "batch_converted_pdfs.zip" : fileItems[0]?.file.name) ||
            "converted-file",
          outputType,
          failedFiles,
        });
        setShowSuccessModal(true);

        if (failedFiles.length > 0) {
          notification.warning({
            message: "Some files failed to convert",
            description: failedFiles
              .map((file) => file.originalFileName || file.fileName || file.name || "Unknown file")
              .join(", "),
          });
        }
      } else {
        message.error(response.data?.message || "Unknown Error");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Conversion failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`i2p-root office-root ${isModule ? "i2p-module" : ""}`}>
        <div className="i2p-shell">
          <div className="i2p-heading">
            <Title level={3}>{title}</Title>
            <Paragraph>{description}</Paragraph>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            hidden
            onChange={handleFileInput}
          />

          {fileItems.length === 0 ? (
            <div
              className={`i2p-empty ${isDragOver ? "drag-over" : ""}`}
              onClick={openFilePicker}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              <div className="i2p-empty-icon">
                <UploadOutlined />
              </div>
              <div className="i2p-empty-title">Choose {documentLabel} Files</div>
              <div className="i2p-empty-sub">
                Select one or multiple files, or drag them here. Multiple files
                are converted one by one and downloaded together as a ZIP.
              </div>
              <button className="i2p-primary-btn" type="button">
                Choose Files
              </button>
            </div>
          ) : (
            <div className="i2p-workspace">
              <div className={`i2p-grid-panel ${uploading ? "locked" : ""}`}>
                <div className="i2p-grid-header">
                  <div>
                    <div className="i2p-grid-title">
                      Selected Files ({fileItems.length})
                    </div>
                    <div className="i2p-grid-sub">
                      Remove files or add more before converting.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="office-clear-btn"
                    onClick={clearFiles}
                    disabled={uploading}
                  >
                    Clear all
                  </button>
                </div>

                <div className="i2p-grid office-file-grid-like-image">
                  {fileItems.map((item) => {
                    const fileType = getOfficeFileType(item.file.name);

                    return (
                      <div key={item.id} className="i2p-card office-doc-card">
                        <div className="i2p-card-actions">
                          <button
                            type="button"
                            className="i2p-card-icon"
                            onClick={() => handleRotate(item.id)}
                            title="Rotate preview"
                            disabled={uploading}
                          >
                            <RotateRightOutlined />
                          </button>
                          <button
                            type="button"
                            className="i2p-card-icon danger"
                            onClick={() => removeFile(item.id)}
                            title="Remove file"
                            disabled={uploading}
                          >
                            <CloseOutlined />
                          </button>
                        </div>
                        <div
                          className={`office-doc-preview ${fileType.className}`}
                        >
                          <span
                            className="office-doc-art"
                            style={{ transform: `rotate(${item.rotation}deg)` }}
                          >
                            <span className="office-doc-icon">{fileType.icon}</span>
                            <span className="office-doc-ext">
                              {getFileExtension(item.file.name).toUpperCase()}
                            </span>
                          </span>
                        </div>
                        <div className="i2p-card-name" title={item.file.name}>
                          {item.file.name}
                        </div>
                        <div className="office-doc-meta">
                          {fileType.label} · {formatFileSize(item.file.size)} · {normalizeRotation(item.rotation)}°
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    className="i2p-add-card"
                    onClick={openFilePicker}
                    disabled={uploading}
                  >
                    <span className="i2p-add-icon">
                      <PlusOutlined />
                    </span>
                    <span className="i2p-add-text">Add more files</span>
                  </button>
                </div>
              </div>

              <aside className="i2p-side-panel">
                <div className="i2p-side-card">
                  <div className="i2p-side-title">{documentLabel} to PDF options</div>
                  <div className="i2p-side-note">
                    Each uploaded Office file is converted into its own PDF.
                    Multiple PDFs are packed together in one ZIP download.
                  </div>

                  <div className="i2p-summary">
                    <div className="i2p-summary-row">
                      <span>Files selected</span>
                      <strong>{fileItems.length}</strong>
                    </div>
                    <div className="i2p-summary-row">
                      <span>Download format</span>
                      <strong>{fileItems.length > 1 ? "ZIP" : "PDF"}</strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="i2p-convert-btn"
                    onClick={handleConvert}
                    disabled={uploading || fileItems.length === 0}
                  >
                    {uploading ? (
                      <>
                        <span className="i2p-spinner" />
                        Converting...
                      </>
                    ) : (
                      "Convert to PDF"
                    )}
                  </button>

                  <OfficePdfResultActions
                    downloadUrl={result?.downloadUrl}
                    originalFileName={result?.fileName}
                    outputType={result?.outputType}
                    requiresAuth={isModule}
                    stacked
                    className="i2p-result-actions"
                  />

                  {result?.failedFiles?.length > 0 && (
                    <div className="office-failed-files">
                      <div className="office-failed-title">Files that failed to convert</div>
                      {result.failedFiles.map((file, index) => (
                        <div key={`${file.originalFileName || file.fileName || index}`} className="office-failed-row">
                          <FileDoneOutlined />
                          <span>{file.originalFileName || file.fileName || file.name || "Unknown file"}</span>
                          {file.message && <small>{file.message}</small>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>

      {!isModule && <FooterLayout />}

      <ConversionSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        fileName={result?.fileName}
        downloadUrl={result?.downloadUrl}
        outputType={result?.outputType}
        requiresAuth={isModule}
        subtitle={`Your ${documentLabel} file${
          fileItems.length > 1 ? "s have" : " has"
        } been converted to PDF successfully.`}
      />
    </>
  );
};

export default OfficeToPdfConverter;
